/**
 * Mock Authentication Helper
 *
 * Utility to set up different user roles for testing entity-specific functionality
 */

import { useAuthStore } from '@/store/authStore';
import { AuthUser, Tenant, PERMISSIONS, Permission, ROLE_PERMISSIONS } from '@/types/shared/auth';

// Mock tenant data
const mockTenant: Tenant = {
  id: 'tenant-001',
  name: 'Sunnydale Community',
  subdomain: 'sunnydale',
  customDomain: 'sunnydale.community',
  logoUrl: '/logo.png',
  primaryColor: '#3b82f6',
  accentColor: '#10b981',
  status: 'active',
  subscriptionTier: 'premium',
  createdAt: '2024-01-01T00:00:00Z',
  updatedAt: new Date().toISOString(),
};

// Mock users for different roles
export const mockUsers = {
  resident: {
    id: 'user-001',
    email: 'resident@example.com',
    firstName: 'John',
    lastName: 'Resident',
    role: 'resident' as const,
    entityType: 'resident',
    tenantId: 'tenant-001',
    permissions: [
      'governance.vote',
      'finance.view_wallet',
      'finance.transfer',
      'marketplace.create_listings',
      'marketplace.view_listings',
      'facilities.book',
      'maintenance.report',
      'communication.send_messages',
      'documents.upload',
      'documents.view',
      'ai.basic',
    ],
    avatarUrl: '/avatars/resident.jpg',
  } as AuthUser,

  moderator: {
    id: 'moderator-001',
    email: 'moderator@example.com',
    firstName: 'Morgan',
    lastName: 'Moderator',
    role: 'moderator' as const,
    entityType: 'community_moderator',
    tenantId: 'tenant-001',
    permissions: ROLE_PERMISSIONS.moderator,
    avatarUrl: '/avatars/moderator.jpg',
  } as AuthUser,

  vendor: {
    id: 'vendor-001',
    email: 'vendor@example.com',
    firstName: 'ABC Services',
    lastName: 'Inc.',
    role: 'vendor' as const,
    entityType: 'vendor',
    tenantId: 'tenant-001',
    permissions: [
      'marketplace.create_listings',
      'marketplace.view_listings',
      'maintenance.manage',
      'maintenance.report',
      'communication.send_messages',
      'documents.upload',
      'documents.view',
      'ai.basic',
    ],
    avatarUrl: '/avatars/vendor.jpg',
  } as AuthUser,

  admin: {
    id: 'admin-001',
    email: 'admin@example.com',
    firstName: 'Jane',
    lastName: 'Manager',
    role: 'admin' as const,
    entityType: 'manager',
    tenantId: 'tenant-001',
    permissions: [
      'users.view',
      'users.create',
      'users.edit',
      'users.manage_roles',
      'tenant.view',
      'tenant.edit',
      'tenant.modules',
      'governance.manage_elections',
      'governance.view_results',
      'finance.view_treasury',
      'finance.manage_treasury',
      'finance.create_invoices',
      'marketplace.manage_listings',
      'facilities.manage',
      'facilities.view_bookings',
      'maintenance.manage',
      'maintenance.assign',
      'communication.announcements',
      'communication.emergency_alerts',
      'documents.manage',
      'ai.advanced',
    ],
    avatarUrl: '/avatars/admin.jpg',
  } as AuthUser,

  super_admin: {
    id: 'super-001',
    email: 'super@example.com',
    firstName: 'Platform',
    lastName: 'Admin',
    role: 'super_admin' as const,
    entityType: 'platform_admin',
    tenantId: 'tenant-001',
    permissions: Object.keys(PERMISSIONS) as Permission[],
    avatarUrl: '/avatars/super.jpg',
  } as AuthUser,

  bank_lender: {
    id: 'bank-001',
    email: 'lender@bank.com',
    firstName: 'Blake',
    lastName: 'Lender',
    role: 'bank_lender' as const,
    entityType: 'bank',
    tenantId: 'tenant-001',
    permissions: ROLE_PERMISSIONS.bank_lender,
    avatarUrl: '/avatars/bank.jpg',
  } as AuthUser,

  platform_support: {
    id: 'support-001',
    email: 'support@example.com',
    firstName: 'Sarah',
    lastName: 'Support',
    role: 'platform_support' as const,
    entityType: 'platform_support',
    tenantId: 'tenant-001',
    permissions: [
      'users.view',
      'users.create',
      'users.edit',
      'tenant.view',
      'tenant.edit',
      'governance.view_results',
      'finance.view_treasury',
      'finance.create_invoices',
      'marketplace.view_listings',
      'marketplace.manage_listings',
      'facilities.view_bookings',
      'maintenance.manage',
      'maintenance.assign',
      'communication.send_messages',
      'communication.announcements',
      'documents.view',
      'documents.upload',
      'documents.manage',
      'ai.advanced',
    ],
    avatarUrl: '/avatars/support.jpg',
  } as AuthUser,
};

/**
 * Initialize mock authentication for testing.
 *
 * Dev-only. This is gated in the function body rather than at the call site because
 * the function is re-exported from the `src/lib/entities/resident` barrel, which makes
 * it a reachable module export in a production bundle regardless of how it is called.
 * See docs/superpowers/specs/2026-08-03-terminal-j-auth-integrity.md
 */
export function initMockAuth(role: keyof typeof mockUsers = 'resident'): void {
  if (!import.meta.env.DEV) {
    console.warn('initMockAuth() is disabled outside development builds.');
    return;
  }

  const { login } = useAuthStore.getState();
  const user = mockUsers[role];

  login(user, mockTenant, {
    token: `mock-token-${role}`,
    refreshToken: `mock-refresh-${role}`,
  });
}

/**
 * Switch to a different user role for testing. Dev-only via initMockAuth.
 */
export function switchRole(role: keyof typeof mockUsers): void {
  initMockAuth(role);
}

/**
 * Get current auth state for debugging
 */
export function getAuthDebugInfo() {
  const state = useAuthStore.getState();
  return {
    isAuthenticated: state.isAuthenticated,
    userRole: state.user?.role,
    userName: state.user ? `${state.user.firstName} ${state.user.lastName}` : undefined,
    permissions: state.user?.permissions?.length || 0,
    tenantName: state.tenant?.name,
  };
}
